import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_CITY_KEY,
  findNearestCity,
  persistLocation,
  readLocationSource,
  readStoredCity,
  readStoredDisplayName,
  resolveStoredCityKey,
  reverseGeocodePlaceName,
  type LocationSource,
} from "@/lib/location";
import { displayNameForTargetCity, useActiveTargetCities, isGpsOutsideActiveMarket, type GpsMarketMismatch } from "@/lib/targetCities";

interface LocationContextValue {
  /** Active city_key for event filtering. */
  location: string;
  /** Label shown in the header (may differ from `location` when GPS is active). */
  locationDisplayName: string;
  /** Set when GPS succeeded but user is outside the nearest active city market. */
  gpsMarketMismatch: GpsMarketMismatch | null;
  setLocation: (cityKey: string) => void;
  isDetecting: boolean;
  locationSource: LocationSource | "default" | null;
  detectAndSetLocation: () => Promise<void>;
  refreshGpsCoords: () => Promise<void>;
  userLat: number | null;
  userLng: number | null;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export function LocationProvider({ children }: { children: ReactNode }) {
  const { data: activeCities = [] } = useActiveTargetCities();
  const [location, setLocationState] = useState<string>(DEFAULT_CITY_KEY);
  const [locationDisplayName, setLocationDisplayName] = useState<string>(DEFAULT_CITY_KEY);
  const [locationSource, setLocationSource] = useState<"gps" | "manual" | "default">("default");
  const [isDetecting, setIsDetecting] = useState<boolean>(() => !readStoredCity());
  const [userLat, setUserLat] = useState<number | null>(null);
  const [userLng, setUserLng] = useState<number | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (activeCities.length === 0) return;

    const stored = readStoredCity();
    const cityKey = resolveStoredCityKey(stored, activeCities);
    const matched = activeCities.find((city) => city.city_key === cityKey);
    setLocationState(cityKey);
    setLocationDisplayName(readStoredDisplayName() ?? (matched ? displayNameForTargetCity(matched) : cityKey));
    setLocationSource(readLocationSource() ?? "default");
    setHydrated(true);
    // Without a stored city we already fell back to the default active market
    // (Zurich). Do not leave isDetecting stuck forever — GPS is only requested
    // from welcome / LocationPicker via detectAndSetLocation.
    if (!stored) {
      setIsDetecting(false);
    }
  }, [activeCities]);

  const gpsMarketMismatch = useMemo((): GpsMarketMismatch | null => {
    if (locationSource !== "gps" || userLat == null || userLng == null) return null;
    if (!isGpsOutsideActiveMarket(userLat, userLng, location, activeCities)) return null;
    const matched = activeCities.find((city) => city.city_key === location);
    if (!matched) return null;
    return {
      detectedPlaceName: locationDisplayName,
      activeCityLabel: displayNameForTargetCity(matched),
    };
  }, [
    locationSource,
    userLat,
    userLng,
    location,
    activeCities,
    locationDisplayName,
  ]);

  const setLocation = useCallback(
    (cityKey: string) => {
      const matched = activeCities.find((city) => city.city_key === cityKey);
      const label = matched ? displayNameForTargetCity(matched) : cityKey;
      setLocationState(cityKey);
      setLocationDisplayName(label);
      setLocationSource("manual");
      setUserLat(null);
      setUserLng(null);
      persistLocation(cityKey, "manual", label);
    },
    [activeCities],
  );

  const detectAndSetLocation = useCallback(async () => {
    if (activeCities.length === 0) return;

    setIsDetecting(true);
    try {
      const coords = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          resolve(null);
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
        );
      });

      if (coords) {
        const cityKey = findNearestCity(coords.lat, coords.lng, activeCities);
        const matched = activeCities.find((city) => city.city_key === cityKey);
        const placeName = await reverseGeocodePlaceName(coords.lat, coords.lng);
        const displayName = placeName ?? (matched ? displayNameForTargetCity(matched) : cityKey);
        setLocationState(cityKey);
        setLocationDisplayName(displayName);
        setLocationSource("gps");
        setUserLat(coords.lat);
        setUserLng(coords.lng);
        persistLocation(cityKey, "gps", displayName);
        return;
      }

      const cityKey = resolveStoredCityKey(readStoredCity(), activeCities);
      const matched = activeCities.find((city) => city.city_key === cityKey);
      setLocationState(cityKey);
      setLocationDisplayName(
        readStoredDisplayName() ?? (matched ? displayNameForTargetCity(matched) : cityKey),
      );
      setUserLat(null);
      setUserLng(null);
    } finally {
      setIsDetecting(false);
    }
  }, [activeCities]);

  const refreshGpsCoords = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          maximumAge: 60000,
        });
      });
      setUserLat(position.coords.latitude);
      setUserLng(position.coords.longitude);
    } catch {
      // silently fail — city-center distance is the fallback
    }
  }, []);

  useEffect(() => {
    if (hydrated && locationSource === "gps" && userLat == null) {
      void refreshGpsCoords();
    }
  }, [hydrated, locationSource, userLat, refreshGpsCoords]);

  const value = useMemo(
    () => ({
      location,
      locationDisplayName,
      gpsMarketMismatch,
      setLocation,
      isDetecting,
      locationSource: hydrated ? locationSource : null,
      detectAndSetLocation,
      refreshGpsCoords,
      userLat,
      userLng,
    }),
    [
      location,
      locationDisplayName,
      gpsMarketMismatch,
      setLocation,
      isDetecting,
      locationSource,
      hydrated,
      detectAndSetLocation,
      refreshGpsCoords,
      userLat,
      userLng,
    ],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocation() {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    throw new Error("useLocation must be used within LocationProvider");
  }
  return ctx;
}
